<template>
  <div class="pedidos-materiales">
    <!-- Header -->
    <div class="pedidos-header">
      <div class="pedidos-header-left">
        <h2 class="pedidos-title">Nuevo pedido de materiales</h2>
        <span class="pedidos-subtitle">{{ listMateriales.length }} materiales en catálogo</span>
      </div>
      <div class="pedidos-header-right">
        <div class="pedidos-view-toggle">
          <button
            :class="['pedidos-view-btn', { active: typeList }]"
            @click="typeList = true"
          >
            <feather-icon icon="ListIcon" size="18" />
          </button>
          <button
            :class="['pedidos-view-btn', { active: !typeList }]"
            @click="typeList = false"
          >
            <feather-icon icon="GridIcon" size="18" />
          </button>
        </div>
      </div>
    </div>

    <!-- Filters bar -->
    <div class="pedidos-filters">
      <div class="pedidos-search-box">
        <feather-icon icon="SearchIcon" size="16" class="pedidos-search-icon" />
        <b-form-input
          placeholder="Buscar material..."
          v-model="buscar"
          class="pedidos-search-input"
        />
      </div>
      <div class="pedidos-filter-group">
        <v-select
          v-model="categoriaSeleccionada"
          label="nombre"
          :options="listCategorias"
          placeholder="Categoria"
          class="pedidos-filter-select"
        />
      </div>
      <div class="pedidos-filter-group">
        <v-select
          v-model="ordenarSelect"
          label="nombre"
          :options="ordenarListado"
          placeholder="Ordenar por"
          class="pedidos-filter-select"
        />
        <div class="pedidos-sort-arrows">
          <feather-icon
            icon="ChevronUpIcon"
            size="18"
            @click="orderDirection = true"
            :class="['pedidos-sort-btn', { active: orderDirection }]"
          />
          <feather-icon
            icon="ChevronDownIcon"
            size="18"
            @click="orderDirection = false"
            :class="['pedidos-sort-btn', { active: !orderDirection }]"
          />
        </div>
      </div>
      <div class="pedidos-filter-group">
        <b-form-checkbox
          v-model="checkPendiente"
          name="check-button"
          switch
          inline
        >
          Pendientes
        </b-form-checkbox>
      </div>
    </div>

    <!-- Main content -->
    <div class="pedidos-content">
      <!-- Left: table or grid -->
      <div class="pedidos-main">
        <!-- Table view -->
        <div v-if="typeList" class="pedidos-table-wrap">
          <b-table
            ref="selectableTableSku"
            :items="listMateriales"
            :fields="columnas"
            class="pedidos-table"
            no-border-collapse
            sort-icon-left
            :fixed="true"
            hover
            @row-clicked="openMaterial"
          >
            <template #cell(stock)="data">
              <b-badge
                :variant="(!data.item.stock && data.item.stock !== 0) ? 'light-secondary' : data.item.stock <= 0 ? 'light-danger' : data.item.pedir ? 'light-warning' : 'light-success'"
                class="pedidos-stock-badge"
              >
                {{ (data.item.stock != null && data.item.stock !== '') ? data.item.stock : '-' }}
              </b-badge>
            </template>
            <template #cell(acciones)="data">
              <b-button
                variant="flat-primary"
                size="sm"
                class="pedidos-add-btn"
                @click.stop="openMaterial(data.item)"
              >
                <feather-icon icon="PlusIcon" size="16" />
              </b-button>
            </template>
          </b-table>
        </div>

        <!-- Grid view -->
        <div v-else class="pedidos-grid">
          <div
            v-for="(item, index) in listMateriales"
            :key="index"
            :class="[
              'pedidos-grid-card',
              {
                'pedidos-grid-card--warning': item.pedir,
                'pedidos-grid-card--success': !item.pedir && item.stock > 0,
                'pedidos-grid-card--danger': !item.pedir && item.stock <= 0,
              }
            ]"
            @click="openMaterial(item)"
          >
            <div class="pedidos-grid-card-body">
              <div class="pedidos-grid-card-top">
                <h6 class="pedidos-grid-card-title">{{ item.nombre }}</h6>
                <b-badge
                  :variant="(!item.stock && item.stock !== 0) ? 'light-secondary' : item.stock <= 0 ? 'danger' : item.pedir ? 'warning' : 'success'"
                  pill
                  class="pedidos-grid-card-stock"
                >
                  {{ (item.stock != null && item.stock !== '') ? item.stock : '-' }}
                </b-badge>
              </div>
              <span v-if="item.color" class="pedidos-grid-card-color">{{ item.color }}</span>
              <div v-if="item.pedir" class="pedidos-grid-card-pedir">
                <div class="pedidos-grid-card-pedir-total">
                  Pedir: <b>{{ item.cantidadPedir }}</b>
                  <span v-if="peticionesDe(item).length <= 1 && item.fechaPedir" class="pedidos-grid-card-fecha"> &middot; {{ item.fechaPedir }}</span>
                </div>
                <div v-if="peticionesDe(item).length > 1" class="pedidos-grid-card-peticiones">
                  <div v-for="p in peticionesDe(item)" :key="p.id" class="pedidos-grid-card-peticion">
                    <span class="pedidos-grid-card-peticion-cant">{{ fmtCantPet(p.cantidad) }}</span>
                    <span class="pedidos-grid-card-fecha">{{ fmtFechaPet(p.fecha) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: cart sidebar -->
      <div class="pedidos-cart">
        <div class="pedidos-cart-inner">
          <div class="pedidos-cart-header">
            <h5 class="pedidos-cart-title">
              <feather-icon icon="ShoppingCartIcon" size="18" />
              Cesta
            </h5>
            <div class="pedidos-cart-header-actions">
              <b-badge v-if="carrito.length > 0" variant="primary" pill>
                {{ carrito.length }}
              </b-badge>
              <button
                v-if="carrito.length > 0"
                class="pedidos-cart-clear"
                @click="vaciarCarrito"
                v-b-tooltip.hover.top
                title="Vaciar cesta"
              >
                <feather-icon icon="Trash2Icon" size="15" />
              </button>
            </div>
          </div>

          <div v-if="carrito.length === 0" class="pedidos-cart-empty">
            <feather-icon icon="ShoppingBagIcon" size="40" class="pedidos-cart-empty-icon" />
            <p>La cesta esta vacia</p>
          </div>

          <div v-else class="pedidos-cart-items">
            <div
              v-for="(proveedor, indexProveedor) in listaProveedores"
              :key="indexProveedor"
              class="pedidos-cart-group"
            >
              <div class="pedidos-cart-proveedor">
                <h6>{{ proveedor }}</h6>
                <b-badge variant="light-secondary" pill>
                  {{ carritoFiltrado(proveedor).length }}
                </b-badge>
              </div>
              <div
                v-for="(item, index) in carritoFiltrado(proveedor)"
                :key="index"
                class="pedidos-cart-item"
              >
                <div class="pedidos-cart-item-info">
                  <span class="pedidos-cart-item-name">{{ item.nombre }}</span>
                  <span v-if="item.color && item.color.length > 0" class="pedidos-cart-item-color">{{ item.color }}</span>
                </div>
                <div class="pedidos-cart-item-actions">
                  <b-badge variant="primary" pill class="pedidos-cart-item-qty">{{ item.cantidad }}</b-badge>
                  <button class="pedidos-cart-item-delete" @click="deleteCarrito(item)">
                    <feather-icon icon="TrashIcon" size="14" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="listaProveedores.length > 0" class="pedidos-cart-footer">
            <b-button variant="success" block @click="generarPedidos()">
              <feather-icon icon="CheckCircleIcon" size="16" class="mr-50" />
              Generar Pedidos
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal cantidad -->
    <b-modal
      ref="modal-cantidad"
      ok-only
      ok-title="Accept"
      centered
      hide-footer
      size="sm"
      modal-class="pedidos-modal"
    >
      <template #modal-title>
        <span class="pedidos-modal-title">{{ nombreMaterialSeleccionado }}</span>
      </template>
      <div class="pedidos-modal-body">
        <div class="pedidos-modal-info">
          <div class="pedidos-modal-info-row" v-if="materialSeleccionado.nombre">
            <span class="pedidos-modal-label">Nombre</span>
            <span class="pedidos-modal-value">{{ materialSeleccionado.nombre }}</span>
          </div>
          <div class="pedidos-modal-info-row" v-if="materialSeleccionado.color">
            <span class="pedidos-modal-label">Color</span>
            <span class="pedidos-modal-value">{{ materialSeleccionado.color }}</span>
          </div>
          <div class="pedidos-modal-info-row">
            <span class="pedidos-modal-label">Stock</span>
            <span class="pedidos-modal-value">
              <b-badge
                :variant="materialSeleccionado.stock <= 0 ? 'light-danger' : 'light-success'"
              >
                {{ materialSeleccionado.stock }}
              </b-badge>
            </span>
          </div>
          <div class="pedidos-modal-info-row" v-if="materialSeleccionado.proveedor">
            <span class="pedidos-modal-label">Proveedor</span>
            <span class="pedidos-modal-value">{{ materialSeleccionado.proveedor }}</span>
          </div>
          <div class="pedidos-modal-info-row" v-if="materialSeleccionado.nota">
            <span class="pedidos-modal-label">Nota</span>
            <span class="pedidos-modal-value">{{ materialSeleccionado.nota }}</span>
          </div>
        </div>

        <hr class="pedidos-modal-divider">

        <div class="pedidos-modal-controls">
          <div class="pedidos-modal-qty-row">
            <button class="pedidos-modal-qty-btn pedidos-modal-qty-btn--minus" @click="cantidadAgregar--">
              <feather-icon icon="MinusIcon" size="20" />
            </button>
            <b-form-input
              v-model="cantidadAgregar"
              type="number"
              class="pedidos-modal-qty-input"
              @wheel.prevent
            />
            <button class="pedidos-modal-qty-btn pedidos-modal-qty-btn--plus" @click="cantidadAgregar++">
              <feather-icon icon="PlusIcon" size="20" />
            </button>
          </div>
          <b-button variant="primary" block class="pedidos-modal-add-btn" @click="guardarEnCesta()">
            <feather-icon icon="ShoppingCartIcon" size="16" class="mr-50" />
            Anadir a la cesta
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>

import { data } from 'vue-echarts';
import { LogInIcon } from 'vue-feather-icons';
import axios from '@axios'
import vSelect from 'vue-select'

export default {

  components: {
    vSelect
    },

  data(){
    return{
      typeList: false,
      listMateriales: [],
      listMaterialesAll: [],
      peticionesPorMaterial: {},
      nombreMaterialSeleccionado: "",
      stockMaterial: "",
      cantidadAgregar: 0,
      materialSeleccionado: {nombre:''},
      carrito: [],
      checkPendiente: false,
      ordenarSelect: null,
      ordenarListado: ["Nombre","Stock","Material","Proveedor","Categoria"],
      listCategorias: ["Fidlocks","Imanes","Herrajes","Cremalleras","Cursores","Tintes","Pegamentos","Cintas refuerzo","Fieltro","Microfibra","RPET","Tejidos","Refuerzos","Cuero","Cintas de mochila"],
      orderDirection: true,
      categoriaSeleccionada: null,
      buscar: "",
      columnas: [
          {
            key: "stock",
            label: "Stock",
            sortable: true,
            thStyle: { width: "10%" },
          },{
            key: "nombre",
            label: "Nombre",
            sortable: true,
          },{
            key: "color",
            label: "Color",
            sortable: true,
          },{
            key: "medidas",
            label: "Medidas",
            sortable: true,
            thStyle: { width: "10%" },
          },{
            key: "proveedor",
            label: "Proveedor",
            sortable: true,
          },{
            key: "acciones",
            label: "",
            sortable: false,
            thStyle: { width: "60px" },
          }],
    }
  },
  computed: {
    listaProveedores() {
      const proveedoresUnicos = [...new Set(this.carrito.map(item => item.proveedor))];
      return proveedoresUnicos;
    },
  },
  methods:{
    carritoFiltrado(proveedor) {
      return this.carrito.filter(item => item.proveedor === proveedor);
    },
    loadItems()
    {
      axios.post("/uso", {seccion:"pedidosNuevo"});
      axios
        .get("/materiales", { params: { sinImg: 1 } })
        .then(response => {
            return response.data;
        })
        .then(res => {
          this.listMaterialesAll = res;
          this.listMateriales = this.listMaterialesAll;
        });
      this.cargarPeticiones();
    },
    // Historial de peticiones (tabla almacenPedidos) agrupado por material, para
    // desglosar en la card las líneas que suman el total de "Pedir".
    cargarPeticiones() {
      axios.get("/almacen/pedidos", { params: { limit: 500 } })
        .then(({ data }) => {
          const map = {};
          (data || []).forEach(p => { (map[p.idMaterial] = map[p.idMaterial] || []).push(p); });
          this.peticionesPorMaterial = map;
        })
        .catch(() => { /* sin desglose: la card sigue mostrando solo el total */ });
    },
    // Las peticiones recientes que suman el total pendiente (el ciclo actual de pedir).
    peticionesDe(material) {
      const lista = this.peticionesPorMaterial[material.id];
      if (!lista || lista.length === 0) return [];
      const total = Number(String(material.cantidadPedir || '0').replace(',', '.')) || 0;
      const res = [];
      let acum = 0;
      for (const p of lista) { // vienen de más reciente a más antigua
        res.push(p);
        acum += Number(p.cantidad) || 0;
        if (acum >= total) break;
      }
      return res;
    },
    fmtFechaPet(f) {
      if (!f) return '';
      const d = new Date(f);
      if (isNaN(d.getTime())) return f;
      const dia = String(d.getDate()).padStart(2, '0');
      const mes = String(d.getMonth() + 1).padStart(2, '0');
      return `${dia}-${mes}-${d.getFullYear()}`;
    },
    fmtCantPet(c) {
      const n = Number(c);
      return isNaN(n) ? c : String(n); // 10.000 -> 10
    },
    openMaterial(data){
      this.materialSeleccionado = data;
      this.nombreMaterialSeleccionado = data.nombre + " " + data.color;
      this.cantidadAgregar = 1;
      this.$refs['modal-cantidad'].show()
    },
    guardarEnCesta(){

      let existeIndex = this.carrito.findIndex(item => item.id === this.materialSeleccionado.id);

      if (existeIndex !== -1) {
        this.carrito[existeIndex].cantidad += this.cantidadAgregar;
      } else {
        this.carrito.push({
          id: this.materialSeleccionado.id,
          idProveedor: this.materialSeleccionado.idProveedorPrincipal,
          nombre: this.materialSeleccionado.nombre,
          color: this.materialSeleccionado.color,
          cantidad: this.cantidadAgregar,
          proveedor: this.materialSeleccionado.proveedor,
          ref: this.materialSeleccionado.ref
        });
      }

      this.$refs['modal-cantidad'].hide()

    },
    async generarPedidos(){
      let pedidos = '';

      for (const element of this.listaProveedores) {
        let res = this.carritoFiltrado(element);

        let resSinProveedor = res.map(item => {
          const { proveedor, idProveedor, ...resto } = item;
          return { ...resto, recibido: false, cantidadRecibida: 0, cantidadFacturada: '' };
        });

        let data = { lista: resSinProveedor, idProveedor: res[0].idProveedor };

        try {
          const response = await axios.post("/materiales/pedido", data);
          const responseData = response.data;
          pedidos += responseData.ref + " ";
        } catch (error) {
          console.error("Error al procesar proveedor:", error);
        }
      }

      this.$swal({
                icon: 'success',
                title: 'Los pedidos se ha creado correctamente: ' + pedidos,
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
            })

      this.carrito = [];
      this.borrarCarritoCookie();

      this.$router.push('/pedido-materiales');

    },
    guardarCarritoCookie(){
      const date = new Date();
      date.setTime(date.getTime() + (30 * 24 * 60 * 60 * 1000));
      const expires = "expires=" + date.toUTCString();
      document.cookie = 'cestaMateriales=' + encodeURIComponent(JSON.stringify(this.carrito)) + ";" + expires + ";path=/";
    },
    cargarCarritoCookie(){
      const nameEQ = 'cestaMateriales=';
      const cookiesArray = document.cookie.split(';');
      for (let i = 0; i < cookiesArray.length; i++) {
        let cookie = cookiesArray[i];
        while (cookie.charAt(0) == ' ') cookie = cookie.substring(1, cookie.length);
        if (cookie.indexOf(nameEQ) == 0) {
          try {
            const datos = JSON.parse(decodeURIComponent(cookie.substring(nameEQ.length, cookie.length)));
            if (Array.isArray(datos)) this.carrito = datos;
          } catch (e) {
            console.error('Error al cargar la cesta guardada:', e);
          }
        }
      }
    },
    borrarCarritoCookie(){
      document.cookie = 'cestaMateriales=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/';
    },
    deleteCarrito(data){
      this.carrito = this.carrito.filter(item => item.id !== data.id);
    },
    vaciarCarrito(){
      if (this.carrito.length === 0) return;
      this.$swal({
        icon: 'warning',
        title: '¿Vaciar la cesta?',
        text: 'Se quitarán todos los materiales añadidos.',
        showCancelButton: true,
        confirmButtonText: 'Sí, vaciar',
        cancelButtonText: 'Cancelar',
        customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) this.carrito = [];
      });
    },
    buscarOrdernar(){
      this.listMateriales = [];
      let auxDatos = JSON.parse(JSON.stringify(this.listMaterialesAll));


      if(this.buscar == "" && this.categoriaSeleccionada == null && !this.checkPendiente)
        this.listMateriales = auxDatos;
      else
      {

        auxDatos.forEach(material => {
          if(material.nombre.toLowerCase().includes(this.buscar.toLowerCase()) ||
            material.categoria.toLowerCase().includes(this.buscar.toLowerCase())
          )
          {

            if(!this.checkPendiente)
            {
              if(this.categoriaSeleccionada == null || material.categoria == this.categoriaSeleccionada)
                this.listMateriales.push(material);
            }
            else
            {
              if((this.categoriaSeleccionada == null || material.categoria == this.categoriaSeleccionada) && material.pedir)
                this.listMateriales.push(material);
            }

          }
        });
      }

      if(this.ordenarSelect != null)
      {
        let sortProp = this.ordenarSelect.toLowerCase();
        if(sortProp != "stock")
        {
          if(this.orderDirection)
            this.listMateriales.sort((a,b) => b[sortProp].localeCompare(a[sortProp]));
          else
            this.listMateriales.sort((a,b) => a[sortProp].localeCompare(b[sortProp]));
        }
        else
        {
          if(this.orderDirection)
            this.listMateriales.sort((a,b) => b[sortProp] - a[sortProp]);
          else
            this.listMateriales.sort((a,b) => a[sortProp] - b[sortProp]);

        }

      }

    }
  },
  watch: {
      carrito: {
        deep: true,
        handler(val){
          if (val.length === 0) this.borrarCarritoCookie();
          else this.guardarCarritoCookie();
        }
      },
      orderDirection(){
        this.buscarOrdernar();
      },
      ordenarSelect(){
        this.buscarOrdernar();
      },
      buscar(){
        this.buscarOrdernar();
      },
      categoriaSeleccionada(){
        this.buscarOrdernar();
      },
      checkPendiente()
      {
        this.buscarOrdernar();
      }
  },
  mounted() {
    this.cargarCarritoCookie();
    this.loadItems();
  },

}
</script>

<style lang="scss">
@import '../@core/scss/vue/libs/vue-select.scss';

/* ============================
   PEDIDOS MATERIALES - REDESIGN
   ============================ */

// ── Sistema de diseño ──────────────────────────────────────────
$pm-accent: #7367f0;
$pm-accent-soft: rgba(115, 103, 240, 0.1);
$pm-ink: #2f2b3d;          // texto principal (neutro con tinte violáceo)
$pm-muted: #8b8a9e;        // texto secundario
$pm-faint: #b8b6c8;        // texto terciario
$pm-surface: #ffffff;
$pm-line: #ecebf3;         // bordes suaves
$pm-bg: #f7f6fb;           // fondos hundidos
$pm-radius: 14px;
$pm-radius-sm: 9px;
$pm-shadow: 0 1px 2px rgba(47, 43, 61, 0.04), 0 4px 16px rgba(47, 43, 61, 0.05);
$pm-shadow-hover: 0 6px 22px rgba(47, 43, 61, 0.1);
$pm-success: #28c76f;
$pm-danger: #ea5455;
$pm-warning: #ff9f43;

.pedidos-materiales {
  padding: 4px 6px;
  color: $pm-ink;
}

/* Header */
.pedidos-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 22px;
}

.pedidos-title {
  margin: 0;
  font-weight: 700;
  font-size: 1.5rem;
  letter-spacing: -0.02em;
  color: $pm-ink;
  line-height: 1.15;
}

.pedidos-subtitle {
  display: block;
  margin-top: 4px;
  font-size: 0.82rem;
  color: $pm-muted;
  font-weight: 500;
}

.pedidos-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pedidos-view-toggle {
  display: flex;
  background: $pm-bg;
  border: 1px solid $pm-line;
  border-radius: 10px;
  padding: 3px;
}

.pedidos-view-btn {
  border: none;
  background: transparent;
  padding: 6px 11px;
  border-radius: 7px;
  cursor: pointer;
  color: $pm-faint;
  display: flex;
  align-items: center;
  transition: all 0.18s ease;

  &.active {
    background: $pm-surface;
    color: $pm-accent;
    box-shadow: 0 1px 4px rgba(47, 43, 61, 0.12);
  }

  &:hover:not(.active) {
    color: $pm-muted;
  }
}

/* Filters */
.pedidos-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 22px;
  padding: 12px 14px;
  background: $pm-surface;
  border-radius: $pm-radius;
  border: 1px solid $pm-line;
  box-shadow: $pm-shadow;
  flex-wrap: wrap;
}

.pedidos-search-box {
  position: relative;
  flex: 1;
  min-width: 220px;
  max-width: 380px;
}

.pedidos-search-icon {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: $pm-faint;
  z-index: 1;
}

.pedidos-search-input {
  padding-left: 40px !important;
  border-radius: $pm-radius-sm !important;
  border: 1px solid $pm-line !important;
  background: $pm-bg !important;
  height: 40px;
  transition: all 0.18s ease;

  &::placeholder { color: $pm-faint; }

  &:focus {
    background: $pm-surface !important;
    border-color: $pm-accent !important;
    box-shadow: 0 0 0 3px $pm-accent-soft !important;
  }
}

.pedidos-filter-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pedidos-filter-select {
  min-width: 158px;

  .vs__dropdown-toggle {
    border-radius: $pm-radius-sm;
    border: 1px solid $pm-line;
    background: $pm-bg;
    min-height: 40px;
    transition: all 0.18s ease;
  }

  &.vs--open .vs__dropdown-toggle {
    border-color: $pm-accent;
    box-shadow: 0 0 0 3px $pm-accent-soft;
  }

  .vs__selected-options { height: 32px; }
  .vs__selected { color: $pm-ink; font-weight: 500; }
}

.pedidos-sort-arrows {
  display: flex;
  flex-direction: column;
  background: $pm-bg;
  border: 1px solid $pm-line;
  border-radius: 8px;
  overflow: hidden;
}

.pedidos-sort-btn {
  cursor: pointer;
  color: $pm-faint;
  transition: all 0.15s ease;
  padding: 1px 3px;

  &.active {
    color: $pm-accent;
    background: $pm-accent-soft;
  }

  &:hover:not(.active) { color: $pm-muted; }
}

/* Main content layout */
.pedidos-content {
  display: flex;
  gap: 22px;
  align-items: flex-start;
}

.pedidos-main {
  flex: 1;
  min-width: 0;
}

/* Table */
.pedidos-table-wrap {
  background: $pm-surface;
  border-radius: $pm-radius;
  border: 1px solid $pm-line;
  box-shadow: $pm-shadow;
  overflow: hidden;
}

.pedidos-table {
  margin-bottom: 0;
  border-collapse: separate;

  thead th {
    background: $pm-bg !important;
    border: none !important;
    font-weight: 600;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    color: $pm-muted;
    padding: 13px 16px;
  }

  tbody td {
    padding: 13px 16px;
    vertical-align: middle;
    border-bottom: 1px solid $pm-line !important;
    border-top: none !important;
    font-size: 13px;
    color: $pm-ink;
  }

  tbody tr {
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover { background-color: rgba(115, 103, 240, 0.04) !important; }
    &:last-child td { border-bottom: none !important; }
  }

  &.table-striped tbody tr:nth-of-type(odd) { background-color: transparent; }
}

.pedidos-stock-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 5px 11px;
  border-radius: 6px;
}

.pedidos-add-btn {
  padding: 5px 9px !important;
  border-radius: 8px !important;
  transition: all 0.18s ease;

  &:hover {
    background: $pm-accent-soft !important;
    transform: scale(1.12);
  }
}

/* Grid view */
.pedidos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 16px;
}

.pedidos-grid-card {
  background: $pm-surface;
  border-radius: $pm-radius;
  border: 1px solid $pm-line;
  box-shadow: $pm-shadow;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    box-shadow: $pm-shadow-hover;
    transform: translateY(-3px);
  }
}

.pedidos-grid-card-body {
  padding: 15px 15px 13px;
}

.pedidos-grid-card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.pedidos-grid-card-stock {
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
}

.pedidos-grid-card-title {
  font-size: 13.5px;
  font-weight: 600;
  color: $pm-ink;
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pedidos-grid-card-color {
  font-size: 12px;
  color: $pm-muted;
  display: block;
  margin-bottom: 4px;
}

.pedidos-grid-card-pedir {
  font-size: 11.5px;
  font-weight: 500;
  color: darken($pm-warning, 6%);
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed $pm-line;
}

.pedidos-grid-card-peticiones {
  margin-top: 5px;
  padding-top: 5px;
  border-top: 1px dotted $pm-line;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.pedidos-grid-card-peticion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 11px;
  color: $pm-muted;

  .pedidos-grid-card-peticion-cant {
    font-weight: 600;
    color: $pm-ink;
  }
}

.pedidos-grid-card-fecha { color: $pm-faint; }

/* Cart sidebar */
.pedidos-cart {
  width: 296px;
  min-width: 296px;
  position: sticky;
  top: 92px;
  max-height: calc(100vh - 110px);
  overflow-y: auto;
}

.pedidos-cart-inner {
  background: $pm-surface;
  border-radius: $pm-radius;
  border: 1px solid $pm-line;
  box-shadow: $pm-shadow;
  padding: 18px;
}

.pedidos-cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid $pm-line;
}

.pedidos-cart-title {
  margin: 0;
  font-weight: 700;
  font-size: 1rem;
  color: $pm-ink;
  display: flex;
  align-items: center;
  gap: 8px;

  svg { color: $pm-accent; }
}

.pedidos-cart-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pedidos-cart-clear {
  border: none;
  background: transparent;
  color: $pm-faint;
  padding: 4px;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  transition: all 0.18s ease;

  &:hover {
    color: $pm-danger;
    background: rgba(234, 84, 85, 0.1);
  }
}

.pedidos-cart-empty {
  text-align: center;
  padding: 36px 10px;
  color: $pm-faint;

  p { margin-top: 12px; font-size: 13px; }
}

.pedidos-cart-empty-icon { opacity: 0.55; }

.pedidos-cart-items { margin-bottom: 14px; }

.pedidos-cart-group {
  margin-bottom: 14px;
  &:last-child { margin-bottom: 0; }
}

.pedidos-cart-proveedor {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  h6 {
    margin: 0;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: $pm-muted;
  }
}

.pedidos-cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 9px 11px;
  margin-bottom: 6px;
  border-radius: $pm-radius-sm;
  background: $pm-bg;
  transition: background 0.15s ease;

  &:hover { background: $pm-accent-soft; }
  &:last-child { margin-bottom: 0; }
}

.pedidos-cart-item-info {
  flex: 1;
  min-width: 0;
}

.pedidos-cart-item-name {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: $pm-ink;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pedidos-cart-item-color {
  font-size: 11px;
  color: $pm-muted;
}

.pedidos-cart-item-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
}

.pedidos-cart-item-qty {
  font-size: 12px;
  min-width: 26px;
  text-align: center;
}

.pedidos-cart-item-delete {
  border: none;
  background: transparent;
  color: $pm-faint;
  padding: 4px;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.18s ease;
  display: flex;
  align-items: center;

  &:hover {
    color: $pm-danger;
    background: rgba(234, 84, 85, 0.1);
  }
}

.pedidos-cart-footer {
  padding-top: 14px;
  margin-top: 4px;
  border-top: 1px solid $pm-line;

  .btn { border-radius: $pm-radius-sm; font-weight: 600; padding: 11px; }
}

/* Modal */
.pedidos-modal {
  .modal-content { border-radius: $pm-radius; border: none; overflow: hidden; }

  .modal-header {
    border-bottom: 1px solid $pm-line;
    padding: 16px 20px;
  }

  .modal-body { padding: 0; }
}

.pedidos-modal-title {
  font-weight: 700;
  font-size: 15px;
  color: $pm-ink;
}

.pedidos-modal-body { padding: 20px; }

.pedidos-modal-info {
  margin-bottom: 10px;
  background: $pm-bg;
  border-radius: $pm-radius-sm;
  padding: 4px 12px;
}

.pedidos-modal-info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid $pm-line;

  &:last-child { border-bottom: none; }
}

.pedidos-modal-label {
  font-size: 11px;
  color: $pm-muted;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  font-weight: 600;
}

.pedidos-modal-value {
  font-size: 13px;
  font-weight: 500;
  color: $pm-ink;
  text-align: right;
}

.pedidos-modal-divider {
  margin: 18px 0;
  border: none;
  border-top: 1px solid $pm-line;
}

.pedidos-modal-controls { text-align: center; }

.pedidos-modal-qty-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
}

.pedidos-modal-qty-btn {
  width: 44px;
  height: 44px;
  border-radius: 13px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s ease;
  color: #fff;

  &--minus {
    background: rgba(234, 84, 85, 0.12);
    color: $pm-danger;
    &:hover { background: $pm-danger; color: #fff; transform: translateY(-1px); }
  }

  &--plus {
    background: rgba(40, 199, 111, 0.12);
    color: $pm-success;
    &:hover { background: $pm-success; color: #fff; transform: translateY(-1px); }
  }
}

.pedidos-modal-qty-input {
  width: 84px !important;
  text-align: center !important;
  font-size: 20px !important;
  font-weight: 700 !important;
  color: $pm-ink !important;
  border-radius: $pm-radius-sm !important;
  border: 1px solid $pm-line !important;
  height: 44px;

  &:focus {
    border-color: $pm-accent !important;
    box-shadow: 0 0 0 3px $pm-accent-soft !important;
  }
}

.pedidos-modal-add-btn {
  padding: 11px !important;
  font-weight: 600;
  border-radius: $pm-radius-sm !important;
}

/* Dark mode */
$pm-d-surface: #283046;
$pm-d-bg: #242b3d;
$pm-d-line: #3b4253;
$pm-d-ink: #d0d2d6;
$pm-d-muted: #8a90a5;

.dark-layout {
  .pedidos-materiales { color: $pm-d-ink; }
  .pedidos-title { color: $pm-d-ink; }
  .pedidos-subtitle { color: $pm-d-muted; }

  .pedidos-view-toggle { background: $pm-d-bg; border-color: $pm-d-line; }
  .pedidos-view-btn {
    color: #676d7d;
    &.active { background: $pm-d-surface; color: $pm-accent; }
  }

  .pedidos-filters { background: $pm-d-surface; border-color: $pm-d-line; }

  .pedidos-search-input {
    background: $pm-d-bg !important;
    border-color: $pm-d-line !important;
    color: $pm-d-ink !important;
    &:focus { background: $pm-d-surface !important; border-color: $pm-accent !important; }
  }

  .pedidos-filter-select .vs__dropdown-toggle { background: $pm-d-bg; border-color: $pm-d-line; }
  .pedidos-filter-select .vs__selected { color: $pm-d-ink; }
  .pedidos-sort-arrows { background: $pm-d-bg; border-color: $pm-d-line; }

  .pedidos-table-wrap { background: $pm-d-surface; border-color: $pm-d-line; }
  .pedidos-table {
    thead th { background: $pm-d-bg !important; color: #b4b7bd; }
    tbody td { border-bottom-color: $pm-d-line !important; color: $pm-d-ink; }
    tbody tr:hover { background-color: rgba(115, 103, 240, 0.08) !important; }
  }

  .pedidos-grid-card {
    background: $pm-d-surface;
    border-color: $pm-d-line;
    &:hover { box-shadow: 0 6px 22px rgba(0, 0, 0, 0.3); }
  }
  .pedidos-grid-card-title { color: $pm-d-ink; }
  .pedidos-grid-card-color { color: $pm-d-muted; }
  .pedidos-grid-card-pedir { border-top-color: $pm-d-line; }
  .pedidos-grid-card-peticiones { border-top-color: $pm-d-line; }
  .pedidos-grid-card-peticion { color: $pm-d-muted; }
  .pedidos-grid-card-peticion .pedidos-grid-card-peticion-cant { color: $pm-d-ink; }

  .pedidos-cart-inner { background: $pm-d-surface; border-color: $pm-d-line; }
  .pedidos-cart-header { border-bottom-color: $pm-d-line; }
  .pedidos-cart-title { color: $pm-d-ink; }
  .pedidos-cart-clear {
    color: #676d7d;
    &:hover { color: $pm-danger; background: rgba(234, 84, 85, 0.15); }
  }
  .pedidos-cart-empty { color: #676d7d; }
  .pedidos-cart-proveedor h6 { color: $pm-d-muted; }
  .pedidos-cart-item {
    background: $pm-d-bg;
    &:hover { background: rgba(115, 103, 240, 0.15); }
  }
  .pedidos-cart-item-name { color: $pm-d-ink; }
  .pedidos-cart-item-color { color: $pm-d-muted; }
  .pedidos-cart-item-delete {
    color: #676d7d;
    &:hover { color: $pm-danger; background: rgba(234, 84, 85, 0.15); }
  }
  .pedidos-cart-footer { border-top-color: $pm-d-line; }

  .pedidos-modal .modal-content { background: $pm-d-surface; }
  .pedidos-modal-title { color: $pm-d-ink; }
  .pedidos-modal-info { background: $pm-d-bg; }
  .pedidos-modal-info-row { border-bottom-color: $pm-d-line; }
  .pedidos-modal-label { color: $pm-d-muted; }
  .pedidos-modal-value { color: $pm-d-ink; }
  .pedidos-modal-divider { border-top-color: $pm-d-line; }
  .pedidos-modal-qty-input {
    background: $pm-d-bg !important;
    border-color: $pm-d-line !important;
    color: $pm-d-ink !important;
  }
}
</style>
